import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Phone, MapPin, CheckCircle, MessageCircle, UserPlus, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "918142445677";
const AREAS = ["Madhapur","Gachibowli","Kondapur","Kukatpally","Miyapur","Banjara Hills","Jubilee Hills","Tolichowki","HITEC City","Begumpet","Ameerpet","Secunderabad","Kokapet","Gandipet","Narsingi"];

const CATEGORIES = {
  "Daily Utility": [{ skill:"Maid", icon:"🏠" },{ skill:"Cook", icon:"🍳" },{ skill:"Driver", icon:"🚗" },{ skill:"Babysitter", icon:"👶" },{ skill:"Elderly Care", icon:"❤️‍🩹" }],
  "Skilled Trades": [{ skill:"Electrician", icon:"⚡" },{ skill:"Plumber", icon:"🔧" },{ skill:"Carpenter", icon:"🪚" },{ skill:"AC Technician", icon:"❄️" },{ skill:"Painter", icon:"🎨" }],
  "Semi-Skilled": [{ skill:"Cleaner", icon:"🧹" },{ skill:"Gardener", icon:"🌱" },{ skill:"Car Wash Worker", icon:"🚿" }],
};
const ALL_SKILLS = Object.values(CATEGORIES).flat().map(s => s.skill);

const HireWorkersPage = () => {
  const [searchParams] = useSearchParams();
  const initialSkill = searchParams.get("skill") || "";
  const [workers, setWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(initialSkill);
  const [filterArea, setFilterArea] = useState("all");
  const [filterAvailable, setFilterAvailable] = useState("all");
  const [filterCat, setFilterCat] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name:"", skill:"", category:"Daily Utility", contact:"", area:"" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/workers`).then(r => setWorkers(r.data)).catch(console.error);
  }, []);

  const filtered = workers.filter(w => {
    const s = !searchTerm || w.skill.toLowerCase().includes(searchTerm.toLowerCase()) || w.name.toLowerCase().includes(searchTerm.toLowerCase());
    const a = filterArea === "all" || w.area.toLowerCase() === filterArea.toLowerCase();
    const av = filterAvailable === "all" || (filterAvailable === "available" && w.available) || (filterAvailable === "busy" && !w.available);
    const c = filterCat === "all" || w.category === filterCat;
    return s && a && av && c;
  });

  const getWA = (w) => `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, I found ${w.name} (${w.skill}) on Junction. I need ${w.skill.toLowerCase()} help in ${w.area}.`)}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.skill || !form.contact) { toast.error("Fill all required fields"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/workers`, form);
      setWorkers(prev => [res.data, ...prev]);
      toast.success("Registered! Verification pending.");
      const waMsg = encodeURIComponent(`New Worker Registration on Junction!\n\nName: ${form.name}\nSkill: ${form.skill}\nPhone: ${form.contact}\nArea: ${form.area || "Not specified"}`);
      window.open(`https://wa.me/${WHATSAPP}?text=${waMsg}`, "_blank");
      setForm({ name:"", skill:"", category:"Daily Utility", contact:"", area:"" });
      setDialogOpen(false);
    } catch { toast.error("Failed."); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <div className="bg-marigold/5 border-b border-marigold/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-indigo mb-1" data-testid="page-heading">Hire Trusted Workers Near You</h1>
          <p className="text-sm text-gray-500">Verified domestic workers, skilled trades & semi-skilled professionals in your neighborhood</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="sticky top-20">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Categories</h3>
              <button onClick={() => { setFilterCat("all"); setSearchTerm(""); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition-colors ${filterCat === "all" ? "bg-indigo text-white" : "text-gray-600 hover:bg-gray-50"}`} data-testid="cat-all">All Workers</button>
              {Object.entries(CATEGORIES).map(([cat, skills]) => (
                <div key={cat} className="mb-3">
                  <button onClick={() => { setFilterCat(cat); setSearchTerm(""); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filterCat === cat ? "bg-indigo/10 text-indigo" : "text-gray-700 hover:bg-gray-50"}`} data-testid={`cat-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}>{cat}</button>
                  <div className="ml-2 mt-1 space-y-0.5">
                    {skills.map(s => (
                      <button key={s.skill} onClick={() => { setSearchTerm(s.skill); setFilterCat("all"); }} className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-indigo hover:bg-gray-50 flex items-center gap-2" data-testid={`skill-${s.skill.toLowerCase().replace(/\s+/g, '-')}`}>
                        <span>{s.icon}</span>{s.skill}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Register CTA in sidebar */}
              <div id="register" className="mt-6 bg-indigo rounded-2xl p-5">
                <h4 className="font-heading text-sm font-medium text-white mb-1">Are you a worker?</h4>
                <p className="text-[11px] text-white/60 mb-3">Register for free. No subscription, no commission.</p>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-marigold text-indigo hover:bg-marigold/90 rounded-full text-xs h-8" data-testid="register-worker-button"><UserPlus size={14} className="mr-1" />Register Now</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md" data-testid="worker-registration-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-lg text-indigo">Register as a Worker</DialogTitle>
                      <DialogDescription>No subscription. No commission. Get hired locally.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                      <Input placeholder="Full Name *" value={form.name} onChange={e => setForm({...form, name:e.target.value})} className="rounded-xl" data-testid="worker-name-input" />
                      <Select value={form.skill} onValueChange={v => setForm({...form, skill:v})}><SelectTrigger className="rounded-xl" data-testid="worker-skill-select"><SelectValue placeholder="Select skill *" /></SelectTrigger><SelectContent>{ALL_SKILLS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                      <Select value={form.category} onValueChange={v => setForm({...form, category:v})}><SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(CATEGORIES).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                      <Input placeholder="Phone *" value={form.contact} onChange={e => setForm({...form, contact:e.target.value})} className="rounded-xl" data-testid="worker-contact-input" />
                      <Input placeholder="Area (e.g., Madhapur)" value={form.area} onChange={e => setForm({...form, area:e.target.value})} className="rounded-xl" data-testid="worker-area-input" />
                      <Button type="submit" disabled={submitting} className="w-full bg-indigo text-white rounded-full" data-testid="worker-submit-button">{submitting ? "Registering..." : "Submit"}</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>

          {/* Main - Worker Cards */}
          <div className="lg:col-span-3">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                <Input placeholder="Search skill or name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 rounded-xl h-10 text-sm border-gray-200" data-testid="worker-search-input" />
              </div>
              <Select value={filterArea} onValueChange={setFilterArea}>
                <SelectTrigger className="w-full sm:w-40 rounded-xl h-10 text-sm border-gray-200" data-testid="worker-area-filter"><SelectValue placeholder="All Areas" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Areas</SelectItem>{AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filterAvailable} onValueChange={setFilterAvailable}>
                <SelectTrigger className="w-full sm:w-36 rounded-xl h-10 text-sm border-gray-200" data-testid="worker-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="available">Available</SelectItem><SelectItem value="busy">Busy</SelectItem></SelectContent>
              </Select>
            </div>

            <p className="text-xs text-gray-400 mb-4">{filtered.length} workers found</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="workers-section">
              {filtered.map(w => (
                <div key={w.id} className="bg-white rounded-2xl p-4 border border-gray-100 hover:shadow-md transition-shadow" data-testid={`worker-card-${w.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-full bg-indigo/5 flex items-center justify-center">
                      <span className="font-heading font-semibold text-indigo text-sm">{w.name.charAt(0)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`status-dot ${w.available ? 'available' : 'busy'}`}></span>
                      <span className={`text-[11px] font-medium ${w.available ? 'text-green-600' : 'text-amber-600'}`}>{w.available ? 'Available' : 'Busy'}</span>
                    </div>
                  </div>
                  <h4 className="font-medium text-indigo text-sm mb-1">{w.name}</h4>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Badge variant="secondary" className="rounded-full text-[11px] bg-gray-50 text-gray-500 font-normal">{w.skill}</Badge>
                    {w.verified && <span className="flex items-center gap-0.5 text-[10px] text-green-600 font-medium"><CheckCircle size={11} />Verified</span>}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-3"><MapPin size={10} />{w.area}</div>
                  {w.available && (
                    <div className="flex gap-2">
                      <a href={`tel:${w.contact}`} className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-xl text-[11px] h-8 border-gray-200 text-indigo hover:bg-indigo hover:text-white" data-testid={`worker-call-${w.id}`}><Phone size={12} className="mr-1" />Call</Button></a>
                      <a href={getWA(w)} target="_blank" rel="noopener noreferrer" className="flex-1"><Button size="sm" className="w-full rounded-xl text-[11px] h-8 bg-green-600 hover:bg-green-700 text-white" data-testid={`worker-whatsapp-${w.id}`}><MessageCircle size={12} className="mr-1" />WhatsApp</Button></a>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {filtered.length === 0 && <p className="text-center py-16 text-gray-400 text-sm" data-testid="no-workers-message">No workers found.</p>}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HireWorkersPage;
